import classNames from 'clsx';
import interpolateComponents from 'interpolate-components';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import getPaymentDetailsCopy from 'src/common/components/shared/copy/getPaymentDetailsCopy';
import { Link } from 'src/common/components/shared/link/Link';
import {
  ACTIVE_NO_BILLING_MONTHLY_COPY,
  ACTIVE_NO_BILLING_YEARLY_COPY,
  ACTIVE_PAYMENT_CHARGED_PRICE_ON_DATE,
  ADD_PAYMENT_METHOD_LINK,
  CANCEL_SUBSCRIPTION_LINK,
  UPDATE_PAYMENT_METHOD_LINK,
} from 'src/common/constants/localizations/subscription';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import billingFrequency from 'src/common/utils/subscription/billingPeriod';
import settingsCss from '../../../account-settings.module.scss';
import css from '../../subscription.module.scss';
import PaymentDetails from '../PaymentDetails';

// exported for unit testing
export const Active = ({
  formattedPrice,
  renewalDate,
  handleCancelSubscriptionButtonClicked,
  billingPeriod,
  hasBillingInfo,
  formattedDiscountPrice,
  couponData,
}) => {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);

  const paymentDetailsWithBilling = () => {
    const paymentDetailsCopy = getPaymentDetailsCopy(billingPeriod, couponData?.durationType);

    return (
      <div>
        <span data-testid="autoRenewCopy">
          {interpolateComponents({
            mixedString: getLocalizedText(paymentDetailsCopy),
            components: {
              date: <span>{renewalDate}</span>,
              price: <span>{formattedDiscountPrice || formattedPrice}</span>,
              discountDurationInMonths: <span>{couponData?.durationInMonths}</span>,
              formattedPrice: <span>{formattedPrice}</span>,
            },
          })}
        </span>
        <div className={css.innerPaymentActions}>
          <div className="row">
            <div className={classNames('col-xs-12', 'col-sm-6', css.alignCenter)}>
              <Link
                id="updatePaymentMethodButton"
                data-testid="updatePaymentMethodButton"
                to="/update/paymentmethod/"
                className={css.link}
                /* biome-ignore lint/a11y/useSemanticElements: invalid lint error for `role` on React Component */
                role="button"
                tabIndex="0"
              >
                {getLocalizedText(UPDATE_PAYMENT_METHOD_LINK)}
              </Link>
            </div>
          </div>
          <div className="row">
            <div className={classNames('col-xs-12', 'col-sm-6', css.alignCenter)}>
              <a
                onClick={handleCancelSubscriptionButtonClicked}
                id="cancelSubscriptionButton"
                data-testid="cancelSubscriptionButton"
                className={css.link}
                /* biome-ignore lint/a11y/useSemanticElements: TODO: explore changing to input or button */
                role="button"
                tabIndex="0"
              >
                {getLocalizedText(CANCEL_SUBSCRIPTION_LINK)}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const paymentDetailsWithoutBilling = () => {
    const paymentDetailsCopy =
      billingPeriod === billingFrequency.yearly
        ? ACTIVE_NO_BILLING_YEARLY_COPY
        : ACTIVE_NO_BILLING_MONTHLY_COPY;

    return (
      <div>
        <span data-testid="addPaymentCopy">
          {interpolateComponents({
            mixedString: getLocalizedText(paymentDetailsCopy),
            components: {
              date: <span>{renewalDate}</span>,
              price: <span>{formattedDiscountPrice || formattedPrice}</span>,
            },
          })}
        </span>
        <div className={css.innerPaymentActions}>
          <div className="row">
            <div className={classNames('col-xs-12', 'col-sm-6', css.alignCenter)}>
              <Link
                id="addPaymentMethodButton"
                data-testid="addPaymentMethodButton"
                to="/update/paymentmethod/"
                className={css.link}
                /* biome-ignore lint/a11y/useSemanticElements: invalid lint error for `role` on React Component */
                role="button"
                tabIndex="0"
              >
                {getLocalizedText(ADD_PAYMENT_METHOD_LINK)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="activeSubscriptionSettings">
      <div className="row">
        {hasBillingInfo && (
          <div
            className={classNames('col-xs-12', 'col-sm-8', settingsCss.activeHeader)}
            data-testid="chargedWithDateCopy"
          >
            {interpolateComponents({
              mixedString: getLocalizedText(ACTIVE_PAYMENT_CHARGED_PRICE_ON_DATE),
              components: {
                date: <strong>{renewalDate}</strong>,
                price: <span>{formattedDiscountPrice || formattedPrice}</span>,
              },
            })}
          </div>
        )}
      </div>
      <div className="row">
        <div className={classNames('col-xs-12', 'col-sm-6', settingsCss.mediumSection)}>
          <PaymentDetails>
            {hasBillingInfo ? paymentDetailsWithBilling() : paymentDetailsWithoutBilling()}
          </PaymentDetails>
        </div>
      </div>
    </div>
  );
};

Active.propTypes = {
  handleCancelSubscriptionButtonClicked: PropTypes.func.isRequired,
  renewalDate: PropTypes.string.isRequired,
  formattedPrice: PropTypes.string.isRequired,
  billingPeriod: PropTypes.string.isRequired,
  hasBillingInfo: PropTypes.bool.isRequired,
  formattedDiscountPrice: PropTypes.string,
  couponData: PropTypes.object,
};
export default Active;
